/**
 * App-shell bridge for the Android Auto Messages tab (mounted once in
 * `NativeBootstrap`, native apps only). Pushes the read-only conversations
 * snapshot into the native CarMessagesStore:
 *
 * 1. Once per app launch after sign-in (boot snapshot, so the car list is
 *    fresh before any message arrives).
 * 2. On every incoming-message event while the app runs, debounced — the
 *    global chime path records each one in `message-notification-store`, so
 *    subscribing there needs no second AppSync subscription.
 *
 * The messages module is imported DYNAMICALLY, mirroring
 * `use-car-sessions-bridge` (NativeBootstrap sits in the web entry chunk).
 * Sign-out wipes the snapshot via auth-service cleanup.
 */

import { useEffect } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { subscribeMessageNotification } from '../../../stores/message-notification-store'
import { isNativeApp } from '../../../utils/platform'

/** One boot-time snapshot push per app launch. */
let bootSnapshotPushed = false

const REPUSH_DEBOUNCE_MS = 2_000

export function useCarMessagesBridge(): void {
  const { user } = useAuth()
  const userId = user?.userId ?? null

  useEffect(() => {
    if (!isNativeApp() || !userId) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const push = () => {
      void import('../utils/car-messages-sync')
        .then((sync) => (cancelled ? undefined : sync.syncCarMessagesSnapshot()))
        .catch(() => {
          /* chunk load failure — retried on the next event */
        })
    }

    if (!bootSnapshotPushed) {
      bootSnapshotPushed = true
      push()
    }

    const unsubscribe = subscribeMessageNotification(() => {
      // Coalesce bursts (group chatter) into one snapshot push.
      if (timer) clearTimeout(timer)
      timer = setTimeout(push, REPUSH_DEBOUNCE_MS)
    })

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      unsubscribe()
    }
  }, [userId])
}

/** Test-only: reset the boot-push latch. */
export function __resetCarMessagesBridgeForTest(): void {
  bootSnapshotPushed = false
}
